import { getAnalytics, isSupported, logEvent, setAnalyticsCollectionEnabled } from 'firebase/analytics'
import type { Analytics } from 'firebase/analytics'
import { app } from '../lib/firebase'

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void
    dataLayer?: unknown[]
  }
}

let analyticsPromise: Promise<Analytics | null> | null = null
let tagLoaded = false

/** Navnet på Google Ads-konverteringen. Må kun sendes ved en fuldført henvendelse. */
export const LEAD_CONVERSION_EVENT = 'conversion_event_submit_lead_form'

/**
 * Sikrer at Google-tagget (gtag.js) er indlæst én gang for hele hjemmesiden.
 * Findes et gtag-snippet allerede på siden — eller har Firebase Analytics
 * indsat det — genbruges det, så tagget aldrig indlæses to gange.
 */
export function ensureGoogleTag(): void {
  if (tagLoaded) return
  if (typeof window === 'undefined' || typeof document === 'undefined') return

  tagLoaded = true

  if (typeof window.gtag === 'function') return
  if (document.querySelector('script[src*="googletagmanager.com/gtag/js"]')) return

  const measurementId = app.options.measurementId
  if (!measurementId) return

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`
  document.head.appendChild(script)

  window.dataLayer = window.dataLayer || []
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer?.push(args)
  }
  window.gtag('js', new Date())
  window.gtag('config', measurementId)
}

/**
 * Initialiserer Google Analytics 4 én gang via den eksisterende Firebase-konfiguration
 * (measurementId G-NSZDNR48EB). Firebase indsætter selv gtag.js, så scriptet
 * tilføjes aldrig to gange. Er gtag allerede indsat på siden, genbruges det.
 */
export function initAnalytics(): Promise<Analytics | null> {
  if (analyticsPromise) return analyticsPromise

  analyticsPromise = isSupported()
    .then((supported) => {
      if (!supported) {
        // Basetagget indlæses alligevel, så konverteringen kan sendes.
        ensureGoogleTag()
        return null
      }
      const analytics = getAnalytics(app)
      setAnalyticsCollectionEnabled(analytics, true)
      ensureGoogleTag()
      return analytics
    })
    .catch(() => {
      ensureGoogleTag()
      return null
    })

  return analyticsPromise
}

/** Sender en GA4-event. Fejler stilfærdigt, så sporing aldrig kan blokere brugerflowet. */
export async function trackEvent(name: string, params: Record<string, unknown> = {}): Promise<void> {
  try {
    const analytics = await initAnalytics()
    if (analytics) {
      logEvent(analytics, name, params)
      return
    }
    // Fallback: et gtag-snippet indsat direkte på siden.
    window.gtag?.('event', name, params)
  } catch {
    /* Sporing må aldrig give fejl for brugeren. */
  }
}

/** Henvendelser der allerede er talt med i denne fane. */
const convertedEnquiries = new Set<string>()
const STORAGE_KEY = 'lykkevaerk.ads.converted'

function alreadyConverted(enquiryId: string): boolean {
  if (convertedEnquiries.has(enquiryId)) return true
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    const ids: unknown = raw ? JSON.parse(raw) : []
    return Array.isArray(ids) && ids.includes(enquiryId)
  } catch {
    return false
  }
}

function rememberConverted(enquiryId: string): void {
  convertedEnquiries.add(enquiryId)
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    const ids: unknown = raw ? JSON.parse(raw) : []
    const next = Array.isArray(ids) ? [...ids, enquiryId] : [enquiryId]
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next.slice(-50)))
  } catch {
    /* Uden sessionStorage bruges kun hukommelsen i denne fane. */
  }
}

/**
 * Google Ads-konvertering. Må KUN kaldes når henvendelsen er oprettet i
 * Firestore, alle billeder er uploadet, stierne er gemt og statussen er
 * "Ny henvendelse". Hvert henvendelses-id tælles højst én gang, så dobbeltklik,
 * gentagne forsøg, rerenders og genindlæsning ikke kan give dubletter.
 */
export function trackLeadConversion(enquiryId: string): void {
  try {
    if (!enquiryId || alreadyConverted(enquiryId)) return
    rememberConverted(enquiryId)

    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', LEAD_CONVERSION_EVENT, {})
      if (import.meta.env?.DEV) {
        // eslint-disable-next-line no-console
        console.info(`Google Ads conversion fired for enquiry: ${enquiryId}`)
      }
      return
    }

    if (import.meta.env?.DEV) {
      // eslint-disable-next-line no-console
      console.warn(`Google Ads conversion skipped — gtag utilgængelig (${enquiryId})`)
    }
  } catch {
    /* Sporing må aldrig forhindre henvendelsen i at blive sendt. */
  }
}

/** Logger at konverteringen bevidst IKKE blev sendt, fordi indsendelsen fejlede. */
export function logConversionSkipped(reason: string): void {
  if (import.meta.env?.DEV) {
    // eslint-disable-next-line no-console
    console.warn(`Google Ads conversion NOT fired — ${reason}`)
  }
}
